import { useEffect } from 'react';
import { Box, Button, Center, Group, Loader, ScrollArea, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowLeft } from '@tabler/icons-react';
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { canAutomate, canViewAdmin } from '~api';
import { useAdminLiveUpdates } from '~portal/agents';
import { api } from '~portal/api';
import {
  activeAdminPath,
  adminNavItems,
  type AdminNavLeaf,
  AdminNavTree,
  leaves,
} from '~portal/components/admin/nav';
import * as navStyles from '~portal/components/admin/nav/AdminNav.css';
import { BrandLogo } from '~portal/components/BrandLogo';
import { NavLinkLink } from '~portal/components/common';
import { getAppEnv } from '~portal/env';
import { AdminAccessProvider } from '~portal/hooks';
import {
  adminAccessOf,
  isPlatform,
  isStandalone,
  logger,
  pageScrollerProps,
  PageScrollers,
} from '~portal/lib';
import * as styles from './route.css';

export const Route = createFileRoute('/_protected/admin')({
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const profileQuery = api.users.me.useQuery();
  const routerState = useRouterState();
  // Live admin data (H2-7, D13): hold the `admin` room while any admin page is open and refetch
  // what each hint names. A no-op unless VITE_AUTOMATIONS_ENABLED is on.
  useAdminLiveUpdates();

  const currentPath = routerState.location.pathname;
  const navItems = adminNavItems(
    getAppEnv().automationsEnabled,
    isPlatform,
    canAutomate(profileQuery.data, 'read'),
  );
  const isMobile = useMediaQuery('(max-width: 62em)'); // Below 'md' breakpoint (992px)
  const activePath = activeAdminPath(currentPath, navItems);

  // Redirect users without admin view access (`admin:view`, D21) once the profile loads
  useEffect(() => {
    if (profileQuery.isSuccess && !canViewAdmin(profileQuery.data)) {
      navigate({ to: '/chat/$conversationId', params: { conversationId: 'new' } }).catch((err) => {
        logger.error('[AdminLayout] Navigation failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    }
  }, [profileQuery.isSuccess, profileQuery.data, navigate]);

  // Show loading state while profile loads
  if (profileQuery.isLoading) {
    return (
      <Center h="100%">
        <Loader size="lg" />
      </Center>
    );
  }

  // Render content only for admin view holders; mutating controls are each page's to disable
  if (!canViewAdmin(profileQuery.data)) {
    return null;
  }
  // What the pages may offer (D21, ADR-29): every page reads it through `useAdminAccess`.
  const access = adminAccessOf(profileQuery.data);

  // On the platform build the admin tree folds over the global nav column — and into its drawer
  // below `md` — so the layout keeps only its content scroller (D3): one navigation column.
  if (isPlatform) {
    return (
      <Stack gap={0} className={styles.adminLayout} data-testid="admin-layout-platform">
        <Box flex={1} className={styles.adminContent} {...pageScrollerProps(PageScrollers.Admin)}>
          <AdminAccessProvider value={access}>
            <Outlet />
          </AdminAccessProvider>
        </Box>
      </Stack>
    );
  }

  // Standalone has no TopNav, so the admin sidebar carries the brand and the way back out.
  const backToAgent = () => {
    navigate({ to: '/chat/$conversationId', params: { conversationId: 'new' } }).catch((err) => {
      logger.error('[AdminLayout] Navigation back to agent failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  const navLeaf = (leaf: AdminNavLeaf) => (
    <NavLinkLink
      key={leaf.path}
      to={leaf.path}
      // Exact only: the router Link otherwise stamps its own active state on any path PREFIX
      // (/admin/users stays lit on /admin/users/subscribers), fighting the `active` prop.
      activeOptions={{ exact: true }}
      label={leaf.label}
      leftSection={leaf.icon}
      active={activePath === leaf.path}
      className={navStyles.navItem}
    />
  );

  if (isMobile) {
    // Below `md` the sidebar becomes a horizontal strip of the flattened destinations.
    return (
      <Stack gap={0} className={styles.adminLayout}>
        <ScrollArea type="never" className={styles.mobileNav}>
          <Group gap={4} px="xs" py={4} wrap="nowrap">
            {isStandalone && (
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconArrowLeft size={16} />}
                onClick={backToAgent}
              >
                Back to agent
              </Button>
            )}
            {leaves(navItems)
              .filter((leaf) => !leaf.desktopOnly)
              .map((leaf) => navLeaf(leaf))}
          </Group>
        </ScrollArea>
        <Box flex={1} className={styles.adminContent} {...pageScrollerProps(PageScrollers.Admin)}>
          <AdminAccessProvider value={access}>
            <Outlet />
          </AdminAccessProvider>
        </Box>
      </Stack>
    );
  }

  return (
    <Group gap={0} align="stretch" wrap="nowrap" className={styles.adminLayout}>
      <Box component="nav" p="sm" className={styles.sidebar} data-testid="admin-sidebar">
        {isStandalone && (
          <Group justify="space-between" wrap="nowrap" px="sm" pt={4} pb="md" gap="xs">
            <BrandLogo height={28} />
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={14} />}
              onClick={backToAgent}
            >
              Back to agent
            </Button>
          </Group>
        )}
        <AdminNavTree items={navItems} path={currentPath} />
      </Box>
      <Box flex={1} className={styles.adminContent} {...pageScrollerProps(PageScrollers.Admin)}>
        <AdminAccessProvider value={access}>
          <Outlet />
        </AdminAccessProvider>
      </Box>
    </Group>
  );
}
